import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from '../../../../../components/GlobalSnackbar/snackbarStore';
import { isSupabaseError } from '../../../../../constants/supabaseErrors';
import type { DisplayedSubjectQuote, DocumentMinimalData } from '../../../../../types';
import { useGetDocumentTypes } from '../../../../documents/api/useGetDocumentTypes';
import { useChangeDocumentsOfResource } from '../../../../documents/hooks/useChangeDocumentsOfResource';
import { keys } from '../../../api/keys';
import { useUpdateSubjectQuoteStatus } from '../../../api/useUpdateSubjectQuoteStatus';

type Props = {
  quote: DisplayedSubjectQuote;
  subjectId: string;
  onClose: () => void;
};

type FormValues = {
  date: Date;
  documents: DocumentMinimalData[];
};

export default function useAcceptQuoteModal(props: Props) {
  const queryClient = useQueryClient();
  const { data: documentTypes } = useGetDocumentTypes();
  const { mutateAsync: updateQuoteStatus, isPending: isUpdatingStatus } = useUpdateSubjectQuoteStatus();
  const { uploadPendingFiles, saveDocumentChanges, isPending: isSavingDocs } = useChangeDocumentsOfResource();

  const [serverError, setServerError] = useState<string | null>(null);

  const bonPourAccordType = documentTypes?.find(
    (dt) => dt.name.toLowerCase() === 'bon pour accord'
  );

  const form = useForm<FormValues>({
    defaultValues: {
      date: props.quote.date_approved ? new Date(props.quote.date_approved) : new Date(),
      documents: [],
    },
  });

  async function handleConfirm(data: FormValues) {
    setServerError(null);

    try {
      let approvedDocumentUrl: string | undefined = undefined;

      if (data.documents.length > 0) {
        const preparedDocs: DocumentMinimalData[] = data.documents.map((doc) => ({
          ...doc,
          document_type_id: doc.document_type_id || bonPourAccordType?.id,
          type: doc.type || bonPourAccordType?.name || 'Bon pour accord',
          provider_id: props.quote.provider_id ?? null,
        }));

        const uploadedDocuments = await uploadPendingFiles(preparedDocs, {
          subjectId: props.subjectId,
        });

        await saveDocumentChanges([], uploadedDocuments, {
          quote_id: props.quote.id,
          sub_topic_id: props.subjectId,
          provider_id: props.quote.provider_id ?? null,
        });

        const firstUploaded = uploadedDocuments.find((d) => d.file_url);
        if (firstUploaded?.file_url) {
          approvedDocumentUrl = firstUploaded.file_url;
        }
      }

      await updateQuoteStatus({
        quoteId: props.quote.id,
        subjectId: props.subjectId,
        status: 'accepted',
        dateApproved: data.date.toISOString(),
        approvedDocumentUrl,
      });

      queryClient.invalidateQueries({ queryKey: keys.subTopicQuotes(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: keys.subTopic(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: [...keys.all, 'activityTimeline'] });

      enqueueSnackbar('Bon pour accord validé.', 'success');
      props.onClose();
    } catch (error: unknown) {
      if (isSupabaseError(error)) {
        setServerError(error.message);
        return;
      }
      setServerError('Une erreur est survenue lors de la validation.');
    }
  }

  const isLoading = isUpdatingStatus || isSavingDocs;

  function handleClose() {
    if (!isLoading) {
      props.onClose();
    }
  }

  const formattedAmount = props.quote.amount != null
    ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(props.quote.amount)
    : null;

  return {
    form,
    handleConfirm,
    handleSubmit: form.handleSubmit(handleConfirm),
    handleClose,
    isLoading,
    serverError,
    formattedAmount,
    providerName: props.quote.providers?.name || props.quote.provider_name || 'Prestataire non renseigné',
    isAlreadyAccepted: props.quote.status === 'accepted' || props.quote.status === 'approved',
  };
}
